import React from "react"
import { Link } from "gatsby"

import Layout from "../../../components/layout"
import { FooterAlternate1 } from "../../../components/footer"
import SEO from "../../../components/seo"

const IndexPage = () => (
    <Layout>
      <SEO title="From extrovert to introvert: Rethinking sales priorities" />
                      
<nav className="share-bar">
    <ul>
        <li className="linkedin">
            <a href="https://www.linkedin.com/shareArticle?mini=true&amp;url=https%3A//salestribe.com/knowledge-centre/insights/extrovert-introvert-rethinking-sales-priorities/&amp;title=From%20extrovert%20to%20introvert%3A%20Rethinking%20sales%20priorities&amp;summary=From%20extrovert%20to%20introvert%3A%20Rethinking%20sales%20priorities&amp;source=https%3A//salestribe.com/knowledge-centre/insights/extrovert-introvert-rethinking-sales-priorities/" target="_blank" rel="noopener noreferrer">
                <span className="sr-only">LinkedIn</span>
                <span className="icon">
                    <svg width="23" height="23">
                        <use href="../../../static/images/svg/symbol-defs.svg#social-linkedin-badge"></use>
                    </svg>
                </span>
            </a>
        </li>
        <li className="twitter">
            <a href="https://twitter.com/intent/tweet/?text=From%20extrovert%20to%20introvert%3A%20Rethinking%20sales%20priorities&amp;url=https%3A//salestribe.com/knowledge-centre/insights/extrovert-introvert-rethinking-sales-priorities/" target="_blank" rel="noopener noreferrer">
                <span className="sr-only">Twitter</span>
                <span className="icon">
                    <svg width="23" height="23">
                        <use href="../../../static/images/svg/symbol-defs.svg#social-twitter-badge"></use>
                    </svg>
                </span>
            </a>
        </li>
        <li className="googleplus">
            <a href="https://plus.google.com/share?url=https%3A//salestribe.com/knowledge-centre/insights/extrovert-introvert-rethinking-sales-priorities/" target="_blank" rel="noopener noreferrer">
                <span className="sr-only">Google+</span>
                <span className="icon">
                    <svg width="26" height="26">
                        <use href="../../../static/images/svg/symbol-defs.svg#social-googleplus-badge"></use>
                    </svg>
                </span>
            </a>
        </li>
        <li className="facebook">
            <a href="https://facebook.com/sharer/sharer.php?u=https%3A//salestribe.com/knowledge-centre/insights/extrovert-introvert-rethinking-sales-priorities/" target="_blank" rel="noopener noreferrer">
                <span className="sr-only">Facebook</span>
                <span className="icon">
                    <svg width="22" height="22">
                        <use href="../../../static/images/svg/symbol-defs.svg#social-facebook-badge"></use>
                    </svg>
                </span>
            </a>
        </li>
        <li className="email">
            <Link to="knowledge-centre/insights/mailto:?subject=From%20extrovert%20to%20introvert%3A%20Rethinking%20sales%20priorities&amp;body=https%3A//salestribe.com/knowledge-centre/insights/extrovert-introvert-rethinking-sales-priorities/" target="_blank" rel="noopener noreferrer">
                <span className="sr-only">Email</span>
                <span className="icon">
                    <svg width="20" height="20">
                        <use href="../../../static/images/svg/symbol-defs.svg#social-email-badge"></use>
                    </svg>
                </span>
            </Link>
        </li>
    </ul>
</nav>

<article className="article">

    

<header className="header header--article article__header" id="page-header">
    <div className="header__image" >
        <div className="header__bgimage" style={{backgroundImage: "url(../../../media/images/article-challenge-juggle_PUKUKDN.2e16d0ba.fill-1162x655.jpg)"}}></div>
        <div className="header__video">
            <div className="embed-responsive embed-responsive-16by9"></div>
        </div>
    </div>
    <div className="header__content">
        <div className="container">
            <div className="row">
                <div className="header__inner col-md-7 col-lg-offset-1">
                    <Link to="knowledge-centre/insights/" className="header__back back-link">
                        <span className="sr-only">Back to all articles</span>
                        <i className="icon"></i>
                    </Link>
                    <div className="header__title">
                        <h3>Sales careers</h3>
                        <h1>From extrovert to introvert: Rethinking sales priorities</h1>
                    </div>
                    <ul className="post-meta-list">
                        <li>by Graham Hawkins</li>
                        <li>2 September 2018</li>
                        <li>4 min read</li>
                    </ul>
                </div>
            </div>
        </div>
    </div>
</header>


<div className="container" id="article-content">
    <div className="row">
        <div className="article__body col-sm-12 col-md-8 col-lg-offset-1 col-lg-7 wysiwyg">
            
<h3>What is the profile of a modern sales person?

This article was written by Graham Hawkins and it first appeared on the Salesforce Blog in July 2018:</h3>

<div className="rich-text"><p>In today’s frenetic business environments, there is a huge premium on time. How and where salespeople allocate time has always been critical because time is a non-renewable resource – once used, it’s gone forever. What’s more, salespeople sometimes mismanage their time because a lot of the world’s sales (and business) leaders are asleep at the wheel. Let me explain:</p><p>According to this year’s <a href="https://www.salesforce.com/form/pdf/state-of-sales-3rd-edition/?d=7010M000000O5guQAC&amp;nc=7010M000000O5gzQAC">State of Sales report</a>, the average Australia and New Zealand sales person is only spending around 31% of their typical working week “selling”. The rest of their time is spent on admin, planning, service, travel and other. What’s perhaps more troubling is that 70% of sales reps surveyed expect to miss their quotas this year.</p><p>With today’s empowered customer, sales activities should revolve around cultivating an acute and ongoing understanding of buyer tastes and preferences, and how you can deliver the most delightful customer experience (CX) possible.</p><p>Accelerating change means that the breakdown of daily activities is now vastly different compared with 5 years ago. Far too many sales people are still performing the same types of activities that they were performing back in 2008, which just won’t cut it today.</p><h2>A new blueprint for sales success is required</h2><p>So what is the right breakdown of sales activities? Before we dive into that, let’s agree on some important points about the future of B2B sales:<br/></p><p></p><ol><li>Buyers no longer accept dealing with a generalist sales person that adds no value. Thus, today’s sales folks must spend a solid percentage of their time honing their specialist knowledge, skills and insights.</li><li>Buyers will avoid engaging with sales people who don’t already know something about them, their business and their pain points. According to the State of Sales, 82% of B2B buyers want the same experience as when they buy for themselves.</li><li>Visibility creates opportunity and if sales people are not visible in the places where buyers are looking for insights (online and social) then they will never profit from the amazing commercial benefits associated with INBOUND leads.</li><li><a href="https://www.salesforce.com/au/blog/2018/06/how-data-and-ai-are-helping-salespeople-boost-revenue.html">Investments in technology need to be made so that salespeople are enabled</a> and encouraged to remove the low value activities from their daily duties. Reducing the travel, admin, order processing, reporting obligations and impost will free up your expensive sales resources to focus on executing those activities that can actually influence the sales results.</li></ol><p></p><h2>Re-thinking the salesperson</h2><p></p><p>As the emphasis on sales activities shifts, so too does the profile of what constitutes an ideal salesperson. The old stereotypes (quota crushers, hunters and lone-wolves) are now making way for a different type of salesperson – a highly analytical, tech savvy, problem solver with deep domain knowledge. This lines up with what B2B buyers reveal they’re after in this year’s <a href="https://www.salesforce.com/form/pdf/state-of-sales-3rd-edition/?d=7010M000000O5guQAC&amp;nc=7010M000000O5gzQAC">State of Sales report</a> as 78% state that they want a trusted advisor - not just a salesperson.</p><p>The salespeople of the future will be digitally driven, socially connected, subject matter experts that can offer their buyers unique perspectives and commercial insights.</p><p>The following roles seem to be converging into what I believe will be a hybrid set of prerequisite competencies for the successful salesperson of the future:</p><ul><li><b>Teacher </b>– buyers want to be educated on solutions to their problems. Selling in the future is now about being a great educator. Remember that you <a href="https://www.linkedin.com/in/davidmeermanscott/">"sell more when you stop selling"</a>, so adapt the demeanour of a teacher.</li><li><b>Data Analyst </b>– sales professionals will need to be able to help customers cut through all the ‘noise’ and find the exact piece of information/data that they need to solve their problem. Developing your skills around data synthesis and being proficient at how and when you disseminate that data will be critical in the future.</li><li><b>Publisher </b>– creating, cultivating and curating educational content will eventually become a prerequisite to sales success, and the A-Graders in many industries are already using content to help establish instant credibility and to drive those much needed high margin "inbound" leads.</li><li><b>Detective </b>- gathering evidence and facts to help solve complex problems is now also a critical skill-set. Interviewing key stakeholders, examining records and identifying value-adding partners is now an important skill-set as the sales role evolves.</li></ul><p></p></div>
<div className="rich-text"><h2>Re-thinking the sales team</h2><p>The role for salespeople is rapidly shifting towards a future where everybody works together for customer success. That is, sales, marketing, pre-sales, product management and customer success people all working together to give the customer the greatest possible buying experience – pre and post the initial sale. In fact, 75% of business buyers surveyed in the State of Sales report say connected processes are important to winning their business.<br/></p><p>It’s time to re-think what it means to be a salesperson in the new customer-led era. Salespeople need to move away from being extroverted quota crushers to more introverted problem-solving analysts. This is how they will succeed in a sales environment where customers are no longer just numbers on a page.</p><p>Looking for sales people who have the right skills in - look no further than SalesTribe. Our sales people are '<i>screen</i>, <i>approved</i>, <i>trained </i>and <i>certified. </i></p><p>Contact SalesTribe today to find out more - graham@salestribe.com <br/></p><p><br/></p></div>


        </div>
            
        <aside className="article__sidebar col-sm-12 col-md-4 col-lg-offset-1 col-lg-3">
            <div className="article__sidebar-wrap" id="article-sidebar">
                <div className="card card--profile">
                        
<div className="card__header">
    <div className="card__image">
        <img alt="Mentor Thumb Graham Hawkins" className="img-responsive" height="165" src="../../../media/images/Mentor-Thumb-Graham-Hawkins.2e16d0ba.fill-165x165.jpg" width="165"/>
    </div>
    <div className="card__title">
        <h4>Graham Hawkins</h4>
        <p>SalesTribe Mentor</p>
        <Link to="solutions/find-a-mentor/graham-hawkins" className="btn btn-link hidden-md hidden-lg">View full profile</Link>
    </div>
</div>
<div className="rich-text"><p>With more than twenty-eight years of business experience in
executive B2B sales and sales leadership roles, Graham is a highly experienced
and versatile business executive with proven strengths in strategic business
development, go-to-market planning and sales and marketing.</p><p><br/></p><p><br/></p></div>
<Link to="solutions/find-a-mentor/graham-hawkins" className="btn btn-link hidden-xs hidden-sm">View full profile</Link>

                </div>
                <nav className="social-nav">
    <h4>Share</h4>
    <ul>
        <li className="linkedin">
            <a href="https://www.linkedin.com/shareArticle?mini=true&amp;url=https%3A//salestribe.com/knowledge-centre/insights/extrovert-introvert-rethinking-sales-priorities/&amp;title=From%20extrovert%20to%20introvert%3A%20Rethinking%20sales%20priorities&amp;summary=From%20extrovert%20to%20introvert%3A%20Rethinking%20sales%20priorities&amp;source=https%3A//salestribe.com/knowledge-centre/insights/extrovert-introvert-rethinking-sales-priorities/" target="_blank" rel="noopener noreferrer">
                <span className="sr-only">LinkedIn</span>
                <span className="icon icon-linkedin"></span>
                
            </a>
        </li>
        <li className="twitter">
            <a href="https://twitter.com/intent/tweet/?text=From%20extrovert%20to%20introvert%3A%20Rethinking%20sales%20priorities&amp;url=https%3A//salestribe.com/knowledge-centre/insights/extrovert-introvert-rethinking-sales-priorities/" target="_blank" rel="noopener noreferrer">
                <span className="sr-only">Twitter</span>
                <span className="icon icon-twitter"></span>
                
            </a>
        </li>
        <li className="googleplus">
            <a href="https://plus.google.com/share?url=https%3A//salestribe.com/knowledge-centre/insights/extrovert-introvert-rethinking-sales-priorities/" target="_blank" rel="noopener noreferrer">
                <span className="sr-only">Google+</span>
                <span className="icon icon-google-plus"></span>
                
            </a>
        </li>
        <li className="facebook">
            <a href="https://facebook.com/sharer/sharer.php?u=https%3A//salestribe.com/knowledge-centre/insights/extrovert-introvert-rethinking-sales-priorities/" target="_blank" rel="noopener noreferrer">
                <span className="sr-only">Facebook</span>
                <span className="icon icon-facebook-f"></span>
                
            </a>
        </li>
        <li className="email">
            <Link to="knowledge-centre/insights/mailto:?subject=From%20extrovert%20to%20introvert%3A%20Rethinking%20sales%20priorities&amp;body=https%3A//salestribe.com/knowledge-centre/insights/extrovert-introvert-rethinking-sales-priorities/" target="_blank" rel="noopener noreferrer">
                <span className="sr-only">Email</span>
                <span className="icon icon-envelope"></span>
                
            </Link>
        </li>
    </ul>
</nav>

            </div>
        </aside>
            
    </div>
</div>
</article>

<div className="block block--grey related-items">
    <div className="related-items__wrap">
        <div className="container">
            <div className="row" data-tablet-slick>
                
        <article className="related-items__item col-sm-6 col-md-4">
            <Link to="knowledge-centre/insights/7-laws-sales-success" className="card card--article">
                <div className="card__thumb">
                    <img alt="article-ideas-announcement.jpg" className="card__image" height="404" src="../../../media/images/article-ideas-announcement_6oOJESq.2e16d0ba.fill-716x404.jpg" width="716"/>
                    <span className="card__image-meta">5 min read</span>
                </div>
                <div className="card__inner">
                    <header>
                        <p className="card__category">Sales careers</p>
                        <h3 className="card__title"><strong>The 7 Laws of Sales Success</strong></h3>
                    </header>
                    <p>There are certain &quot;Laws&quot; of the universe which successful people often leverage, and today&#39;s&#39; sales people must learn to embrace some of these important laws to ensure future sales success.</p>
                    <footer><p className="card__author">Graham Hawkins</p></footer>
                </div>
            </Link>
        </article><article className="related-items__item col-sm-6 col-md-4">
            <Link to="knowledge-centre/insights/sales-outreach-destroying-your-career" className="card card--article">
                <div className="card__thumb">
                    <img alt="article-challenge-yell.jpg" className="card__image" height="404" src="../../../media/images/article-challenge-yell_YbkW7pV.2e16d0ba.fill-716x404.jpg" width="716"/>
                    <span className="card__image-meta">5 min read</span>
                </div>
                <div className="card__inner">
                    <header>
                        <p className="card__category">Sales careers</p>
                        <h3 className="card__title"><strong>Is sales Outreach destroying your Career?</strong></h3>
                    </header>
                    <p>If you are still performing 20th century outreach tactics like &quot;cold calling&quot; then you are not just destroying your own career, but you are also destroying your employers brand and therefore their business.</p>
                    <footer><p className="card__author">Graham Hawkins</p></footer>
                </div>
            </Link>
        </article><article className="related-items__item col-sm-6 col-md-4">
            <Link to="knowledge-centre/insights/new-golden-age-b2b-sales-has-arrived" className="card card--article">
                <div className="card__thumb">
                    <img alt="Success golden egg" className="card__image" height="404" src="../../../media/images/article-success-golden-egg.2e16d0ba.fill-716x404.jpg" width="716"/>
                    <span className="card__image-meta">5 min read</span>
                </div>
                <div className="card__inner">
                    <header>
                        <p className="card__category">Sales careers</p>
                        <h3 className="card__title"><strong>The new golden age of B2B sales has arrived.</strong></h3>
                    </header>
                    <p>So much of the sales role is still lived in a fog of automatic and habitual behaviour due largely to outdated leadership that has failed to recognise this new customer-led era that we now live in.</p>
                    <footer><p className="card__author">Graham Hawkins</p></footer>
                </div>
            </Link>
        </article>

            </div>
            <div className="load-more">
                <Link to="knowledge-centre/insights/" className="btn btn-link">Browse more insights</Link>
            </div>
        </div>
    </div>
</div>
      
      <FooterAlternate1 />

    </Layout>
)

export default IndexPage 